import * as React from 'react';
import { useStore, type FriendlyKind, TUTORIAL_TASKS } from '../state/store';
import type { UnitOrder } from '../game/Entity';
import { simClock } from '../game/clock';

/** Friend/foe marker palette. Default green-vs-red is invisible to red-green colorblind players
 *  (~8% of men) — the colorblind palette swaps to blue-vs-orange, distinguishable by every common
 *  type of color vision deficiency. */
const PAL_DEFAULT = { enemy: '#ff3b30', friendly: '#7be77b', gun: '#7ad6ff' };
const PAL_CB = { enemy: '#ff9d1c', friendly: '#4aa8ff', gun: '#9fe0ff' };
function usePal() {
  const cb = useStore((s) => s.colorblind);
  return cb ? PAL_CB : PAL_DEFAULT;
}

/** Coarse pointer ≈ touch device. Picks which control hints to show (touch+pad vs keyboard). */
const COARSE_POINTER = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
import { GameOver } from './GameOver';
import { Hangar } from './Hangar';

interface Props {
  onRestart: () => void;
  /** Start the interactive coached tutorial (no-fail sandbox). */
  onTutorial: () => void;
  /** Cycle control to the prev (-1) / next (+1) allied unit. */
  onJump: (dir: 1 | -1) => void;
  /** Jump directly to a unit by entity id. */
  onSelectUnit: (id: number) => void;
  /** Jump to whichever unit is best placed for the current top threat. */
  onJumpCrisis: () => void;
  /** Open/close the fullscreen tactical map. */
  onToggleMap: () => void;
  /** Issue a standing order to a unit from the map. */
  onIssueOrder: (unitId: number, order: UnitOrder) => void;
}

export function Hud({ onRestart, onTutorial, onJump, onSelectUnit, onJumpCrisis, onToggleMap, onIssueOrder }: Props) {
  const phase = useStore((s) => s.phase);
  const score = useStore((s) => s.score);
  const hiScore = useStore((s) => s.hiScore);
  const wave = useStore((s) => s.wave);
  const hp = useStore((s) => s.hp);
  const maxHp = useStore((s) => s.maxHp);
  const waveIntroAt = useStore((s) => s.waveIntroAt);
  const hitFlashAt = useStore((s) => s.hitFlashAt);
  const muted = useStore((s) => s.muted);
  const toggleMute = useStore((s) => s.toggleMute);
  const ai = useStore((s) => s.ai);
  const toggleAi = useStore((s) => s.toggleAi);
  const enemyArrow = useStore((s) => s.enemyArrow);
  const hideMarks = useStore((s) => s.hideMarks);
  const villagesAlive = useStore((s) => s.villagesAlive);
  const villagesTotal = useStore((s) => s.villagesTotal);
  const combo = useStore((s) => s.combo);
  const comboFlashAt = useStore((s) => s.comboFlashAt);
  const comboFlashText = useStore((s) => s.comboFlashText);
  const reinforceBanked = useStore((s) => s.reinforceBanked);
  const reinforceEtaSec = useStore((s) => s.reinforceEtaSec);
  const setPhase = useStore((s) => s.setPhase);
  const reducedMotion = useStore((s) => s.reducedMotion);
  const tutorial = useStore((s) => s.tutorial);

  // Animate wave intro / hit flash / combo pops. Throttled to ~30Hz instead of every
  // frame — the HUD (incl. the SVG radar) doesn't need 60fps and this halves its cost
  // on low-end phones.
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    let raf = 0;
    let last = 0;
    const tick = (ts: number) => {
      if (ts - last >= 33) { last = ts; force(); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const now = performance.now() / 1000; // wall clock for the hit-flash + combo pops (stamped on wall)
  // waveIntroAt is stamped on the SIM clock by the game loop, so measure elapsed against the same
  // clock (not performance.now) — otherwise the two clocks' different origins break the flash.
  const waveIntroElapsed = simClock.now - waveIntroAt;
  const showWaveFlash = waveIntroElapsed >= 0 && waveIntroElapsed < 2.0 && phase === 'playing' && !tutorial;
  let waveFlashOpacity = 0;
  if (showWaveFlash) {
    const t = waveIntroElapsed / 2.0;
    waveFlashOpacity = t < 0.5 ? t * 2 : (1 - t) * 2;
  }
  const hitFlashElapsed = now - hitFlashAt;
  const hitFlashOpacity = Math.max(0, 1 - hitFlashElapsed / 0.45) * (reducedMotion ? 0.28 : 0.55);

  const hpPct = Math.max(0, (hp / maxHp) * 100);
  const hpColor = hpPct <= 20 ? '#e74c3c' : hpPct <= 40 ? '#f39c12' : '#2ecc71';

  // Combo indicator
  const comboMult = Math.min(5, 1 + Math.floor(combo / 5) * 0.5);
  const comboFlashElapsed = now - comboFlashAt;
  const comboPop = Math.max(0, 1 - comboFlashElapsed / 0.5);

  // The Hangar fully replaces the play view.
  if (phase === 'hangar') {
    return <Hangar onLaunch={onRestart} onBack={() => setPhase('splash')} />;
  }

  return (
    <div style={{ position: 'absolute', inset: 0, color: '#fff', pointerEvents: 'none', fontFamily: 'inherit' }}>
      {/* Hit-flash red vignette */}
      <div style={{
        position: 'absolute', inset: 0,
        boxShadow: 'inset 0 0 200px 60px rgba(220,30,30,1)',
        opacity: hitFlashOpacity,
        transition: 'opacity 0.08s linear',
        pointerEvents: 'none',
      }} />

      {/* Top banner — hidden on the splash so it can't collide with the title block. */}
      {phase !== 'splash' && <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        padding: '10px 12px',
        background: 'rgba(0,0,0,0.5)',
        textAlign: 'center',
        fontSize: 14,
        letterSpacing: 1,
        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
      }}>
        {tutorial ? 'TRAINING SORTIE' : <>WAVE {wave}  •  SCORE {score}  •  HI {Math.max(hiScore, score)}</>}
        <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2, letterSpacing: 1 }}>
          {tutorial ? 'LIVE-FIRE SYSTEMS · SAFE MUNITIONS' : <>VILLAGES <span style={{
            color: villagesAlive <= 5 ? '#ff5555' :
                   villagesAlive <= 10 ? '#ffb84a' : '#a3e6a3',
          }}>{villagesAlive}/{villagesTotal}</span></>}
        </div>
      </div>}

      {/* Earned reinforcements: milestones bank planes, an arrival cooldown delivers them. A
          standalone pill below the banner — the countdown to a replacement is a command decision
          (who to protect, where to fall back) so it must be readable mid-fight, not buried. */}
      {phase === 'playing' && (reinforceEtaSec != null || reinforceBanked > 0) && (
        <div style={{
          // Below the radar block (radar ends ~y200) so it's clear of every top-strip element on phones.
          position: 'absolute', top: 206, left: '50%', transform: 'translateX(-50%)',
          padding: '5px 14px', borderRadius: 14, whiteSpace: 'nowrap',
          background: 'rgba(0,0,0,0.6)',
          border: `1px solid ${reinforceEtaSec != null ? 'rgba(127,211,255,0.7)' : 'rgba(159,224,159,0.6)'}`,
          fontSize: 13, fontWeight: 800, letterSpacing: 1.5,
          color: reinforceEtaSec != null ? '#7fd3ff' : '#9fe09f',
          textShadow: '0 1px 2px #000',
        }}>
          {reinforceEtaSec != null
            ? `⟳ REINFORCEMENT INBOUND · ${reinforceEtaSec}s`
            : `⟳ RESERVE ×${reinforceBanked} — AWAITING LOSSES`}
        </div>
      )}

      {/* HP bar above the nose */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: '78%',
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          width: '55%', height: 10,
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 3,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${hpPct}%`,
            height: '100%',
            background: hpColor,
            transition: 'width 0.15s ease-out, background-color 0.15s',
          }} />
        </div>
      </div>

      {/* Combo meter — top center, under the banner. Shows current streak + multiplier. */}
      {phase === 'playing' && combo >= 2 && (
        <div style={{
          position: 'absolute', top: 52, left: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            fontSize: reducedMotion ? 26 : 26 + comboPop * 14, fontWeight: 900, letterSpacing: 2,
            color: comboMult >= 3 ? '#ff5a3a' : comboMult >= 2 ? '#ffae3a' : '#ffe14a',
            textShadow: '0 0 12px rgba(0,0,0,0.8), 0 2px 3px #000',
            transform: reducedMotion ? 'none' : `scale(${1 + comboPop * 0.15})`,
          }}>
            ×{combo}
          </div>
          <div style={{ fontSize: 11, letterSpacing: 3, opacity: 0.85, marginTop: -2, textShadow: '0 1px 2px #000' }}>
            {comboMult.toFixed(1)}× SCORE
          </div>
        </div>
      )}

      {/* Combo / near-miss flash text */}
      {phase === 'playing' && comboPop > 0 && comboFlashText && (
        <div style={{
          position: 'absolute', top: '40%', left: 0, right: 0,
          textAlign: 'center', pointerEvents: 'none',
          fontSize: 30, fontWeight: 800, letterSpacing: 4,
          color: comboFlashText === 'CLOSE!' ? '#82d8ff' : '#ffe14a',
          opacity: comboPop,
          textShadow: '0 0 14px rgba(0,0,0,0.9), 0 2px 4px #000',
          transform: reducedMotion ? 'none' : `translateY(${(1 - comboPop) * -18}px)`,
        }}>
          {comboFlashText}
        </div>
      )}

      {/* Wave flash */}
      {showWaveFlash && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          opacity: waveFlashOpacity,
          textShadow: '0 2px 12px rgba(0,0,0,0.8)',
        }}>
          <div style={{ fontSize: 48, fontWeight: 700, letterSpacing: 4 }}>WAVE {wave}</div>
          {/* Wave 1 restates the objective so the player starts with the mission in mind. */}
          {wave === 1 && (
            <div style={{ marginTop: 8, fontSize: 18, fontWeight: 700, letterSpacing: 2, color: '#ffe6b0', textShadow: '0 2px 4px #000' }}>
              DEFEND YOUR VILLAGES
            </div>
          )}
          {(() => {
            // Boss flash subtitle. The rotation matches WaveDirector.bossForWave.
            if (wave < 3 || wave % 3 !== 0) return null;
            const idx = (wave / 3 - 1) % 8;
            const kinds = [
              { name: '⚔ ACE INCOMING ⚔', color: '#ffe14a', shadow: 'rgba(255,200,40,0.7)' },
              { name: '☠ SKY CARRIER ☠', color: '#ff5a3a', shadow: 'rgba(255,90,50,0.8)' },
              { name: '☁ AETHER DREADNOUGHT ☁', color: '#9bd6ff', shadow: 'rgba(120,180,255,0.85)' },
              { name: '⚡ TESLA BASTION ⚡', color: '#82d8ff', shadow: 'rgba(120,180,255,0.85)' },
              { name: '◆ THE JUGGERNAUT ◆', color: '#a050ff', shadow: 'rgba(160,80,255,0.85)' },
              { name: '⚙ CLOCKWORK COLOSSUS ⚙', color: '#ffae3a', shadow: 'rgba(255,150,40,0.85)' },
              { name: '~ THE IRON WORM ~', color: '#9b6f3a', shadow: 'rgba(180,120,60,0.8)' },
              { name: '⌖ MAELSTROM ENGINE ⌖', color: '#b284ff', shadow: 'rgba(180,120,255,0.85)' },
            ];
            const k = kinds[idx];
            return (
              <div style={{
                marginTop: 8,
                fontSize: 22, fontWeight: 800, letterSpacing: 5,
                color: k.color,
                textShadow: `0 0 12px ${k.shadow}, 0 2px 4px #000`,
              }}>{k.name}</div>
            );
          })()}
        </div>
      )}

      {/* On-screen target reticles — hidden during The Long Dark (fight by radar + muzzle-flash). */}
      {phase === 'playing' && !hideMarks && <EnemyMarks />}

      {/* Off-screen enemy arrow */}
      {phase === 'playing' && !hideMarks && enemyArrow.any && enemyArrow.offscreen && (
        <EnemyArrow angle={enemyArrow.angle} />
      )}

      {/* Radar (top-left) */}
      {phase === 'playing' && <Radar />}

      {/* Afterburner — hold to boost ~3× speed to rush across the map. Bottom-left so it
          sits under the off-hand thumb, opposite the AI/mute controls. */}
      {phase === 'playing' && <BoostButton />}
      {phase === 'playing' && <RpmGauge />}
      {phase === 'playing' && <FireButton />}

      {/* Gunsight crosshair when manning a ground gun (looking down the barrel). */}
      {phase === 'playing' && <Gunsight />}

      {/* Tank targeting computer — lock box + lead marker for the crewed tank. */}
      {phase === 'playing' && <TankTargetingHUD />}

      {/* Threat alert banner — directional callout when something needs your attention. */}
      {phase === 'playing' && <ThreatBanner />}

      {/* Transient control-scheme hint after taking over a unit. */}
      {phase === 'playing' && <ControlHint />}

      {/* MAP button — opens the live fullscreen tactical command surface. */}
      {phase === 'playing' && <MapButton onToggleMap={onToggleMap} />}

      {/* Fullscreen tactical map overlay — tap a unit to jump or issue a standing order. */}
      <TacticalMap onSelectUnit={onSelectUnit} onToggleMap={onToggleMap} onIssueOrder={onIssueOrder} />

      {/* Commander jump bar — switch which allied unit you're flying/manning. */}
      {phase === 'playing' && <JumpBar onJump={onJump} onSelectUnit={onSelectUnit} onJumpCrisis={onJumpCrisis} />}

      {/* Autopilot toggle for the currently possessed unit — uncrewed allies always use their AI. */}
      {phase === 'playing' && <button
        data-testid="autopilot-toggle"
        onClick={toggleAi}
        style={{
          position: 'absolute', right: 10, top: 92,
          padding: '6px 10px',
          background: ai ? 'rgba(40,140,60,0.85)' : 'rgba(0,0,0,0.5)',
          border: `1px solid ${ai ? 'rgba(160,255,180,0.8)' : 'rgba(255,255,255,0.35)'}`,
          color: '#fff',
          borderRadius: 4,
          fontFamily: 'inherit',
          fontSize: 11,
          letterSpacing: 1,
          fontWeight: 700,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}>
        AUTOPILOT {ai ? 'ON' : 'OFF'}
      </button>}

      {/* Mute button */}
      {phase === 'playing' && <button
        onClick={toggleMute}
        style={{
          position: 'absolute', right: 10, top: 130,
          padding: '6px 10px',
          background: 'rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff',
          borderRadius: 4,
          fontFamily: 'inherit',
          fontSize: 13,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}>
        {muted ? 'UNMUTE' : 'MUTE'}
      </button>}

      {/* Tutorial coach checklist — only during the no-fail tutorial sandbox. */}
      {phase === 'playing' && <TutorialCoach onFinish={onRestart} />}

      {phase === 'splash' && <Splash onTutorial={onTutorial} />}
      {phase === 'gameover' && <CrashSequence onRestart={onRestart} />}
      {phase === 'victory' && <GameOver onRestart={onRestart} />}

      {/* Random-event colour wash (overcharge amber, gremlin green, …) under the white flash. */}
      <EventTint />
      {/* Cinematic event announcement card. */}
      <EventCard />
      {/* UFO white-out — must paint over EVERYTHING (last child, top z-index). */}
      <WhiteFlash />
    </div>
  );
}

// Cinematic announcement card for a random event — a big title + tagline that sweeps in on flanking
// rules, holds, and fades. Self-animated via rAF only while a card is live.
function EventCard() {
  const card = useStore((s) => s.eventCard);
  const reduced = useStore((s) => s.reducedMotion);
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    if (!card) return;
    let raf = 0;
    const tick = () => { if (performance.now() / 1000 - card.at < 4) { force(); raf = requestAnimationFrame(tick); } };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [card]);
  if (!card) return null;
  const el = performance.now() / 1000 - card.at;
  if (el > 3.9) return null;
  const inK = Math.min(1, el / 0.45);
  const outK = el > 2.9 ? Math.max(0, 1 - (el - 2.9) / 0.85) : 1;
  const op = inK * outK;
  const scale = reduced ? 1 : 0.86 + 0.14 * inK;
  const ruleW = (reduced ? 1 : inK) * 240;
  const rule = (
    <div style={{ width: ruleW, height: 2, margin: '0 auto', background: `linear-gradient(90deg, transparent, ${card.color}, transparent)`, boxShadow: `0 0 8px ${card.color}` }} />
  );
  return (
    <div style={{
      position: 'absolute', left: '50%', top: '20%', transform: `translateX(-50%) scale(${scale})`,
      textAlign: 'center', pointerEvents: 'none', zIndex: 58, opacity: op, whiteSpace: 'nowrap',
    }}>
      {rule}
      <div style={{
        margin: '8px 0 4px', fontWeight: 900, letterSpacing: 7,
        fontSize: 'clamp(24px, 6vw, 44px)', color: card.color,
        textShadow: `0 0 18px ${card.color}, 0 0 40px ${card.color}88, 0 2px 4px #000`,
      }}>{card.title}</div>
      <div style={{
        fontSize: 'clamp(11px, 2.4vw, 15px)', letterSpacing: 3, fontWeight: 700,
        color: 'rgba(255,255,255,0.9)', textShadow: '0 0 10px rgba(0,0,0,0.9), 0 1px 2px #000', marginBottom: 8,
      }}>{card.sub}</div>
      {rule}
    </div>
  );
}

// Full-screen colour wash for random events (amber overcharge, green gremlins, …). Own component
// so only it re-renders as the tint changes. Soft-transitions so tints fade rather than pop.
function EventTint() {
  const tint = useStore((s) => s.eventTint);
  return (
    <div style={{
      position: 'absolute', inset: 0, background: tint || 'transparent',
      opacity: tint ? 1 : 0, transition: 'opacity 0.6s ease', pointerEvents: 'none', zIndex: 55,
      mixBlendMode: 'multiply',
    }} />
  );
}

// Full-screen white flash for the UFO encounter. Its own component so only it re-renders as the
// flash animates. Only mounts a live div when there's something to show.
function WhiteFlash() {
  const w = useStore((s) => s.whiteout);
  if (w <= 0.001) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, background: '#ffffff',
      opacity: Math.min(1, w), pointerEvents: 'none', zIndex: 60,
    }} />
  );
}

/** Staged opening-mission card. Only the current action is visible; completed beats collapse into
 *  four progress pips so the cockpit remains readable on a phone. */
function TutorialCoach({ onFinish }: { onFinish: () => void }) {
  const tutorial = useStore((s) => s.tutorial);
  const done = useStore((s) => s.tutorialDone);
  if (!tutorial) return null;
  const allDone = TUTORIAL_TASKS.every((t) => done[t.key]);
  const narrowHud = window.innerWidth < 620;
  const stage = !done.flight ? 0 : !done.scout ? 1 : !done.bomber ? 2 : 3;
  let title = TUTORIAL_TASKS[stage].label;
  let instruction = '';
  let detail = '';
  if (!done.flight) {
    if (!done.steer) { instruction = COARSE_POINTER ? 'DRAG TO BANK AND TURN' : 'USE WASD / ARROWS TO STEER'; detail = 'Put the aircraft on a new heading.'; }
    else if (!done.climb) { instruction = 'PULL BACK TO CLIMB'; detail = 'Then push forward to lower the nose.'; }
    else { instruction = COARSE_POINTER ? 'HOLD BOOST' : 'HOLD SHIFT TO BOOST'; detail = 'Use speed to cross the front quickly.'; }
  } else if (!done.scout) {
    instruction = COARSE_POINTER ? 'HOLD FIRE ON THE MARKED SCOUT' : 'HOLD SPACE ON THE MARKED SCOUT';
    detail = 'Lead the target; safe munitions are active.';
  } else if (!done.bomber) {
    if (!done.gun) { instruction = 'BOMBER INBOUND — PRESS ⚠ JUMP'; detail = 'Take the AA gun nearest the threatened village.'; }
    else { instruction = COARSE_POINTER ? 'AIM THE AA GUN · HOLD FIRE' : 'AIM WITH WASD · HOLD SPACE'; detail = 'Destroy the bomber before it reaches the village.'; }
  } else if (!done.order) {
    if (!done.map) { instruction = COARSE_POINTER ? 'OPEN 🗺 MAP' : 'PRESS M TO OPEN THE MAP'; detail = 'Armor is advancing on the flashing village.'; }
    else { instruction = 'SELECT A TANK, THEN THE FLASHING VILLAGE'; detail = 'Issue a DEFEND order to hold the road.'; }
  }
  if (allDone) {
    title = 'SORTIE COMPLETE'; instruction = 'YOU CAN FLY, FIGHT, JUMP, AND COMMAND';
    detail = 'The real war begins at Wave 1.';
  }
  return (
    <div data-testid="tutorial-coach" data-stage={stage + 1} style={{
      position: 'absolute', left: '50%', top: narrowHud ? 206 : 58, transform: 'translateX(-50%)',
      width: narrowHud ? 'calc(100vw - 24px)' : 'min(440px, calc(100vw - 210px))',
      padding: '10px 14px', borderRadius: 8, boxSizing: 'border-box',
      background: 'rgba(5,10,15,0.84)', border: '1px solid rgba(255,210,120,0.55)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.34)',
      display: 'flex', flexDirection: 'column', gap: 5, pointerEvents: 'none',
      textAlign: 'center', textShadow: '0 1px 3px #000',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 10, letterSpacing: 2, fontWeight: 800, color: '#ffe6b0' }}>
          TRAINING · {Math.min(stage + 1, 4)}/4
        </span>
        <span style={{ display: 'flex', gap: 5 }}>
          {TUTORIAL_TASKS.map((t, i) => <span key={t.key} style={{
            width: 24, height: 3, borderRadius: 2,
            background: done[t.key] ? '#73df91' : i === stage ? '#ffd36f' : 'rgba(255,255,255,0.22)',
          }} />)}
        </span>
      </div>
      <div style={{ fontSize: 12, letterSpacing: 2.2, fontWeight: 900, color: allDone ? '#83eda0' : '#fff' }}>{title}</div>
      <div style={{ fontSize: 14, letterSpacing: 1, fontWeight: 800, color: '#ffd36f' }}>{instruction}</div>
      <div style={{ fontSize: 11, lineHeight: 1.35, color: '#d6e1ea', opacity: 0.9 }}>{detail}</div>
      {allDone && (
        <button
          onClick={onFinish}
          style={{
            marginTop: 8, padding: '8px 14px',
            background: 'rgba(40,140,60,0.9)', border: '1px solid rgba(160,255,180,0.8)',
            color: '#fff', borderRadius: 6, fontFamily: 'inherit', fontSize: 13,
            letterSpacing: 1, fontWeight: 800, cursor: 'pointer', pointerEvents: 'auto',
            alignSelf: 'center',
          }}>
          START THE WAR ▸
        </button>
      )}
    </div>
  );
}

function CrashSequence({ onRestart }: { onRestart: () => void }) {
  const deathStartedAt = useStore((s) => s.deathStartedAt);
  const deathImpactAt = useStore((s) => s.deathImpactAt);
  const [skipped, setSkipped] = React.useState(false);
  const now = performance.now() / 1000;
  const elapsed = deathStartedAt > 0 ? now - deathStartedAt : 0;
  const sinceImpact = deathImpactAt > 0 ? now - deathImpactAt : -1;

  // Big screaming headline appears immediately and fades; retry overlay comes later.
  const headlineOpacity =
    elapsed < 0.1 ? elapsed / 0.1 :
    elapsed < 2.5 ? 1 :
    elapsed < 3.5 ? 1 - (elapsed - 2.5) : 0;

  // Retry overlay shows after ground impact + short pause — or instantly via SKIP.
  const showRetry = skipped || sinceImpact > 0.9 || elapsed > 6;

  return (
    <>
      {headlineOpacity > 0 && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
          opacity: headlineOpacity,
        }}>
          <div style={{
            fontSize: 64, fontWeight: 900, letterSpacing: 8,
            color: '#ff2a2a',
            textShadow: '0 3px 0 #400, 0 0 24px rgba(255,30,30,0.7), 0 0 2px #000',
            transform: `scale(${1 + Math.max(0, 0.4 - elapsed) * 0.3})`,
          }}>
            SHOT DOWN
          </div>
          <div style={{
            marginTop: 14,
            fontSize: 16, letterSpacing: 4, color: '#ffd8d8',
            textShadow: '0 1px 2px #000',
          }}>
            YOUR FORCE IS BROKEN
          </div>
        </div>
      )}
      {/* Slow red vignette that intensifies on impact */}
      <div style={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none',
        boxShadow: 'inset 0 0 260px 80px rgba(120,0,0,1)',
        opacity: Math.min(0.6, 0.15 + elapsed * 0.08 + (sinceImpact > 0 ? 0.35 : 0)),
      }} />
      {showRetry && <GameOver onRestart={onRestart} />}
      {/* Skip the death cinematic — straight to the score panel (ENTER also restarts). */}
      {!showRetry && elapsed > 0.8 && (
        <button
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); setSkipped(true); }}
          style={{
            position: 'absolute', right: 14, bottom: 16,
            padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
            background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.4)',
            color: '#fff', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, letterSpacing: 2,
            pointerEvents: 'auto', touchAction: 'none',
          }}>
          SKIP ▸
        </button>
      )}
    </>
  );
}

function EnemyMarks() {
  const P = usePal();
  useStore((s) => s.radarTick);
  const marks = useStore.getState().enemyMarks;
  return (
    <>
      {marks.filter((m) => m.visible).map((m) => {
        const leftPct = (m.ndcX * 0.5 + 0.5) * 100;
        const topPct = (1 - (m.ndcY * 0.5 + 0.5)) * 100;
        // Reticle grows as target approaches, shrinks at distance — gives a quick read
        // on range without needing to look at the text. Clamp so far dots stay visible
        // and close targets don't swallow the screen.
        const t = Math.max(0, Math.min(1, 1 - (m.dist - 80) / 900));
        const box = m.kind === 'zeppelin' ? 78 : 30 + t * 48;
        const color = m.dying ? '#888' : (m.inCone ? '#ffe14a' : P.enemy);
        const stroke = m.inCone && !m.dying ? 3 : 2;
        return (
          <div key={m.id} style={{
            position: 'absolute',
            left: `${leftPct}%`,
            top: `${topPct}%`,
            width: box, height: box,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}>
            <svg width={box} height={box} viewBox="0 0 100 100">
              {/* corner brackets */}
              <polyline points="6,28 6,6 28,6" fill="none" stroke={color} strokeWidth={stroke * 1.2} />
              <polyline points="72,6 94,6 94,28" fill="none" stroke={color} strokeWidth={stroke * 1.2} />
              <polyline points="94,72 94,94 72,94" fill="none" stroke={color} strokeWidth={stroke * 1.2} />
              <polyline points="28,94 6,94 6,72" fill="none" stroke={color} strokeWidth={stroke * 1.2} />
            </svg>
            <div style={{
              position: 'absolute',
              left: '100%', top: '50%', transform: 'translate(6px, -50%)',
              color,
              fontSize: 10, letterSpacing: 1, fontWeight: 700,
              whiteSpace: 'nowrap',
              textShadow: '0 1px 2px rgba(0,0,0,0.9)',
            }}>
              {m.kind === 'zeppelin' ? 'ZEPPELIN' : ''}{m.kind === 'zeppelin' ? ' ' : ''}{Math.round(m.dist)}
            </div>
          </div>
        );
      })}
    </>
  );
}

function FireButton() {
  const firing = useStore((s) => s.manualFiring);
  const set = useStore.getState().setManualFiring;
  const start = (e: React.PointerEvent) => { e.stopPropagation(); e.preventDefault(); set(true); };
  const end = (e: React.PointerEvent) => { e.stopPropagation(); set(false); };
  return (
    <button
      onPointerDown={start}
      onPointerUp={end}
      onPointerLeave={end}
      onPointerCancel={end}
      style={{
        position: 'absolute', right: 12, bottom: 92,
        width: 84, height: 84, borderRadius: '50%',
        background: firing ? 'radial-gradient(circle at 50% 40%, #ff6b5e, #c01808)' : 'rgba(60,0,0,0.45)',
        border: `2px solid ${firing ? '#ffc0b8' : 'rgba(255,120,120,0.55)'}`,
        color: firing ? '#fff' : '#ffd9d9',
        fontFamily: 'inherit', fontSize: 13, fontWeight: 800, letterSpacing: 1,
        cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
        boxShadow: firing ? '0 0 22px rgba(255,60,40,0.85)' : 'none',
        userSelect: 'none',
      }}>
      FIRE
    </button>
  );
}

// Polar point + SVG arc-path helpers for the round gauges (y-down, sweep clockwise).
function _polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}
function _arc(cx: number, cy: number, r: number, d0: number, d1: number): string {
  const [x0, y0] = _polar(cx, cy, r, d0);
  const [x1, y1] = _polar(cx, cy, r, d1);
  const large = d1 - d0 > 180 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

// Engine tachometer — a brass RPM dial that doubles as the stall warning. The needle tracks
// airspeed/cruise; a red arc marks the stall band at the low end. As revs bleed into it (steep
// climb), the needle redlines, the dial pulses, and the stall horn beeps (both in PlayerPlane).
function RpmGauge() {
  const rpm = useStore((s) => s.rpmFrac);
  const warn = useStore((s) => s.stallWarn);
  const reducedMotion = useStore((s) => s.reducedMotion);
  const C = 38, R = 30, A0 = 150, SWEEP = 240, MAXF = 1.5; // 240° sweep, gap at the bottom
  const dz = (v: number) => A0 + Math.min(1, v / MAXF) * SWEEP;
  const [nx, ny] = _polar(C, C, R - 4, dz(rpm));
  const stalling = warn > 0.001;
  return (
    <div style={{
      position: 'absolute', left: 18, bottom: 188, width: 76, height: 76, pointerEvents: 'none',
      filter: stalling ? 'drop-shadow(0 0 8px rgba(255,50,40,0.85))' : 'none',
      animation: stalling && !reducedMotion ? 'stallpulse 0.5s ease-in-out infinite' : 'none',
    }}>
      <svg width="76" height="76" viewBox="0 0 76 76">
        <circle cx={C} cy={C} r={R + 5} fill="rgba(0,0,0,0.55)" stroke="rgba(190,155,90,0.55)" strokeWidth="1.5" />
        {/* zone arcs: stall (red) · warn (amber) · normal (green) · overspeed (cyan) */}
        <path d={_arc(C, C, R, dz(0), dz(0.35))} stroke={stalling ? '#ff3b30' : '#c8402f'} strokeWidth="4" fill="none" />
        <path d={_arc(C, C, R, dz(0.35), dz(0.55))} stroke="#e0a020" strokeWidth="4" fill="none" />
        <path d={_arc(C, C, R, dz(0.55), dz(1.2))} stroke="#4bd06a" strokeWidth="4" fill="none" />
        <path d={_arc(C, C, R, dz(1.2), dz(1.5))} stroke="#5ac8ff" strokeWidth="4" fill="none" />
        <line x1={C} y1={C} x2={nx.toFixed(2)} y2={ny.toFixed(2)} stroke={stalling ? '#ff3b30' : '#ffe6b0'} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx={C} cy={C} r="3.4" fill="#caa25a" />
        <text x={C} y={C + 15} textAnchor="middle" fontSize="8" fontWeight="800" letterSpacing="1"
          fill={stalling ? '#ff6a5a' : 'rgba(255,230,180,0.8)'}>{stalling ? 'STALL' : 'RPM'}</text>
      </svg>
    </div>
  );
}

function BoostButton() {
  const boosting = useStore((s) => s.boosting);
  const heat = useStore((s) => s.boostHeat);
  const overheat = useStore((s) => s.boostOverheat);
  const set = useStore.getState().setBoost;
  const start = (e: React.PointerEvent) => { e.stopPropagation(); e.preventDefault(); set(true); };
  const end = (e: React.PointerEvent) => { e.stopPropagation(); set(false); };
  const lit = boosting && !overheat;
  const warn = lit && heat > 0.72; // burner about to trip
  // Heat ring: a bold annulus that fills 0→360° as the burner loads, sitting just OUTSIDE the
  // button so it isn't drowned by the boost glow. Amber→red by load; solid red when tripped.
  const ringCol = overheat ? '#ff2a1a' : heat > 0.72 ? '#ff3a1a' : heat > 0.45 ? '#ff7a1a' : '#ffc24a';
  const ring = overheat
    ? `conic-gradient(${ringCol} 360deg, ${ringCol} 0)`
    : `conic-gradient(${ringCol} ${Math.round(heat * 360)}deg, rgba(255,255,255,0.12) 0)`;
  // The button ALSO heats amber→red as load climbs, so you read the trip coming without hunting
  // for the ring — the primary tell during boost, when the glow would otherwise hide the ring.
  const fill = overheat ? 'radial-gradient(circle at 50% 40%, #6b1408, #2a0a04)'
    : warn ? 'radial-gradient(circle at 50% 40%, #ff9a3a, #ff2e10)'
    : lit ? 'radial-gradient(circle at 50% 40%, #ffd24a, #ff7a1a)' : 'rgba(0,0,0,0.45)';
  const glow = overheat ? '0 0 18px rgba(255,40,20,0.65)'
    : warn ? '0 0 26px rgba(255,60,20,0.95)'
    : lit ? '0 0 20px rgba(255,140,30,0.8)' : 'none';
  return (
    <div style={{ position: 'absolute', left: 12, bottom: 92, width: 84, height: 84, pointerEvents: 'none' }}>
      {/* Heat ring behind the button — a 6px annulus (masked conic) sitting just past the rim. */}
      <div style={{
        position: 'absolute', inset: -6, borderRadius: '50%', background: ring,
        WebkitMask: 'radial-gradient(circle, transparent 43px, #000 44px)',
        mask: 'radial-gradient(circle, transparent 43px, #000 44px)',
        opacity: heat > 0.02 || overheat ? 1 : 0, transition: 'opacity 0.2s',
      }} />
      <button
        onPointerDown={start}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        style={{
          position: 'absolute', inset: 0,
          width: 84, height: 84, borderRadius: '50%',
          background: fill,
          border: `2px solid ${overheat ? '#ff5a3a' : warn ? '#ff7a4a' : lit ? '#ffe9a8' : 'rgba(255,255,255,0.4)'}`,
          color: overheat ? '#ffb0a0' : lit ? '#3a1a00' : '#fff',
          fontFamily: 'inherit', fontSize: overheat ? 11 : 13, fontWeight: 800, letterSpacing: 1,
          cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
          boxShadow: glow,
          userSelect: 'none',
        }}>
        {overheat ? 'HEAT' : 'BOOST'}
      </button>
    </div>
  );
}

function TankTargetingHUD() {
  const tt = useStore((s) => s.tankTargeting);
  if (!tt) return null;
  const sx = (ndcX: number) => (ndcX * 0.5 + 0.5) * 100;
  const sy = (ndcY: number) => (-ndcY * 0.5 + 0.5) * 100;
  const col = tt.onSolution ? '#37e06a' : '#ffb23a'; // green on firing solution, amber while leading
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {/* Lock box bracketing the target. */}
      {tt.lockVisible && (
        <div style={{ position: 'absolute', left: `${sx(tt.lockNdcX)}%`, top: `${sy(tt.lockNdcY)}%`, transform: 'translate(-50%,-50%)' }}>
          <svg width="60" height="60" viewBox="0 0 60 60">
            <path d="M6 18 V6 H18 M42 6 H54 V18 M54 42 V54 H42 M18 54 H6 V42"
              fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="2"
              style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.8))' }} />
          </svg>
          <div style={{
            position: 'absolute', top: 32, left: '50%', transform: 'translateX(-50%)',
            whiteSpace: 'nowrap', color: col, fontFamily: 'inherit', fontSize: 11, fontWeight: 800,
            letterSpacing: 1, textShadow: '0 1px 2px #000',
          }}>RNG {Math.round(tt.range)}</div>
        </div>
      )}
      {/* Lead marker — put the barrel (centre crosshair) on this to hit the moving target. */}
      {tt.leadVisible && (
        <div style={{
          position: 'absolute', left: `${sx(tt.leadNdcX)}%`, top: `${sy(tt.leadNdcY)}%`,
          transform: 'translate(-50%,-50%)', filter: `drop-shadow(0 0 4px ${col})`,
        }}>
          <svg width="46" height="46" viewBox="0 0 46 46">
            <circle cx="23" cy="23" r="12" fill="none" stroke={col} strokeWidth="2.5" />
            <line x1="23" y1="2" x2="23" y2="12" stroke={col} strokeWidth="2" />
            <line x1="23" y1="34" x2="23" y2="44" stroke={col} strokeWidth="2" />
            <line x1="2" y1="23" x2="12" y2="23" stroke={col} strokeWidth="2" />
            <line x1="34" y1="23" x2="44" y2="23" stroke={col} strokeWidth="2" />
            <circle cx="23" cy="23" r="2.2" fill={col} />
          </svg>
        </div>
      )}
      {/* Status line. */}
      <div style={{
        position: 'absolute', left: '50%', top: '15%', transform: 'translateX(-50%)',
        color: col, fontFamily: 'inherit', fontSize: 13, fontWeight: 800, letterSpacing: 2,
        textShadow: '0 1px 2px #000',
      }}>{tt.onSolution ? '◈ ON SOLUTION' : '◈ LEAD TARGET'}</div>
    </div>
  );
}

function Gunsight() {
  const reticle = useStore((s) => s.controlledReticle);
  const firing = useStore((s) => s.isFiring);
  if (reticle !== 'crosshair') return null;
  const col = firing ? '#ff4438' : 'rgba(255,255,255,0.85)';
  const glow = firing ? 'drop-shadow(0 0 4px rgba(255,60,40,0.9))' : 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))';
  return (
    <div style={{
      position: 'absolute', left: '50%', top: '50%',
      transform: 'translate(-50%, -50%)',
      pointerEvents: 'none', filter: glow,
    }}>
      <svg width="200" height="200" viewBox="0 0 200 200">
        {/* Outer sighting ring + finer inner ring */}
        <circle cx="100" cy="100" r="64" fill="none" stroke={col} strokeWidth="1.5" opacity="0.7" />
        <circle cx="100" cy="100" r="30" fill="none" stroke={col} strokeWidth="1.5" />
        {/* Cross hairs with a centre gap */}
        <line x1="100" y1="20" x2="100" y2="70" stroke={col} strokeWidth="2" />
        <line x1="100" y1="130" x2="100" y2="180" stroke={col} strokeWidth="2" />
        <line x1="20" y1="100" x2="70" y2="100" stroke={col} strokeWidth="2" />
        <line x1="130" y1="100" x2="180" y2="100" stroke={col} strokeWidth="2" />
        {/* Range ticks on the lower post */}
        <line x1="92" y1="140" x2="108" y2="140" stroke={col} strokeWidth="1.5" opacity="0.8" />
        <line x1="94" y1="155" x2="106" y2="155" stroke={col} strokeWidth="1.5" opacity="0.7" />
        <line x1="96" y1="170" x2="104" y2="170" stroke={col} strokeWidth="1.5" opacity="0.6" />
        {/* Centre pip */}
        <circle cx="100" cy="100" r="2.2" fill={col} />
      </svg>
    </div>
  );
}

function ThreatBanner() {
  const alert = useStore((s) => s.alert);
  const reducedMotion = useStore((s) => s.reducedMotion);
  // 8 Hz repaint so the pulsing/arrow stays smooth without re-rendering at 60 fps.
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    const id = setInterval(() => force(), 125);
    return () => clearInterval(id);
  }, []);
  if (!alert) return null;
  const pulse = reducedMotion ? 0.8 : 0.6 + 0.4 * Math.sin(performance.now() / 1000 * 6);
  const col = alert.severity === 'high' ? '#ff4438' : '#ffae3a';
  // Arrow angle: 0 = right, π/2 = up. Rotate the triangle so it points outward.
  const rotDeg = (-alert.angleRad * 180) / Math.PI + 90;
  return (
    <div style={{
      position: 'absolute', top: 78, left: 0, right: 0,
      display: 'flex', justifyContent: 'center', pointerEvents: 'none',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '6px 14px',
        background: 'rgba(0,0,0,0.6)',
        border: `1px solid ${col}`,
        borderRadius: 6,
        color: col,
        font: '700 14px/1 inherit',
        letterSpacing: 2,
        boxShadow: `0 0 ${10 + pulse * 14}px ${col}55`,
      }}>
        {!alert.inFront && (
          <svg width="22" height="22" viewBox="0 0 22 22"
            style={{ transform: `rotate(${rotDeg}deg)`, filter: 'drop-shadow(0 1px 1px #000)' }}>
            <polygon points="11,2 19,18 11,14 3,18" fill={col} stroke="#fff" strokeWidth="1.2" />
          </svg>
        )}
        <span>{alert.label}</span>
      </div>
    </div>
  );
}

function JumpBar({ onJump, onSelectUnit, onJumpCrisis }: {
  onJump: (dir: 1 | -1) => void;
  onSelectUnit: (id: number) => void;
  onJumpCrisis: () => void;
}) {
  const units = useStore((s) => s.controlUnits);
  const controlledId = useStore((s) => s.controlledId);
  const alert = useStore((s) => s.alert);
  const narrowHud = window.innerWidth < 620;
  if (units.length <= 1) return null;

  const idx = Math.max(0, units.findIndex((u) => u.id === controlledId));
  const cur = units[idx];
  const hpFrac = cur?.hpFrac ?? 1;
  const hpColor = hpFrac <= 0.2 ? '#ff4b3e' : hpFrac <= 0.4 ? '#f0a33a' : '#55d67a';
  const groups = [
    { key: 'air', kind: 'plane' as FriendlyKind, label: 'AIR', icon: '▲' },
    { key: 'armor', kind: 'tank' as FriendlyKind, label: 'ARMOR', icon: '■' },
    { key: 'defense', kind: 'gun' as FriendlyKind, label: 'DEFENSE', icon: '⊙' },
  ];
  const currentGroup = groups.find((g) => g.kind === cur?.kind) ?? groups[0];
  const currentGroupUnits = units.filter((u) => u.kind === currentGroup.kind);
  const groupIndex = Math.max(0, currentGroupUnits.findIndex((u) => u.id === controlledId));

  const selectGroup = (kind: FriendlyKind) => {
    const group = units.filter((u) => u.kind === kind);
    if (group.length === 0) return;
    const active = group.findIndex((u) => u.id === controlledId);
    onSelectUnit(group[active >= 0 ? (active + 1) % group.length : 0].id);
  };
  const arrowBtn = (label: string, dir: 1 | -1) => (
    <button
      aria-label={dir < 0 ? 'Previous unit' : 'Next unit'}
      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onJump(dir); }}
      style={{
        width: narrowHud ? 28 : 34, alignSelf: 'stretch', border: 0,
        background: 'rgba(13,24,31,0.92)', color: '#a9dfff',
        fontSize: 17, fontWeight: 900, cursor: 'pointer',
        pointerEvents: 'auto', touchAction: 'none',
      }}>{label}</button>
  );

  return (
    <div data-testid="command-bar" style={{
      position: 'absolute', left: '50%', bottom: 14, transform: 'translateX(-50%)',
      width: narrowHud ? 'calc(100vw - 18px)' : 'min(520px, calc(100vw - 150px))',
      display: 'flex', alignItems: 'stretch', gap: 6, pointerEvents: 'none',
      filter: 'drop-shadow(0 5px 12px rgba(0,0,0,0.65))',
    }}>
      <div style={{
        minWidth: 0, flex: 1, overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(11,20,27,0.96), rgba(4,10,14,0.94))',
        border: '1px solid rgba(207,160,82,0.68)', borderRadius: 5,
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          {groups.map((group) => {
            const count = units.filter((u) => u.kind === group.kind).length;
            const active = currentGroup.kind === group.kind;
            return (
              <button key={group.key}
                data-testid={`command-group-${group.key}`}
                disabled={count === 0}
                aria-label={`${group.label} units: ${count}`}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); selectGroup(group.kind); }}
                style={{
                  minWidth: 0, padding: narrowHud ? '5px 2px' : '6px 8px',
                  border: 0, borderRight: group.key === 'defense' ? 0 : '1px solid rgba(255,255,255,0.1)',
                  background: active ? 'rgba(42,125,162,0.48)' : 'rgba(255,255,255,0.02)',
                  boxShadow: active ? 'inset 0 -2px #65d5ff' : 'none',
                  color: count === 0 ? 'rgba(255,255,255,0.26)' : active ? '#c7efff' : '#9aa8b1',
                  font: `800 ${narrowHud ? 8 : 9}px/1 inherit`, letterSpacing: narrowHud ? 0.7 : 1.3,
                  cursor: count ? 'pointer' : 'default', pointerEvents: 'auto', touchAction: 'none',
                }}>
                <span style={{ marginRight: 4 }}>{group.icon}</span>{group.label} <span style={{ color: active ? '#fff' : '#d9e0e4' }}>{count}</span>
              </button>
            );
          })}
        </div>
        <div style={{ minHeight: narrowHud ? 52 : 56, display: 'flex', alignItems: 'stretch' }}>
          {arrowBtn('‹', -1)}
          <div style={{ minWidth: 0, flex: 1, padding: narrowHud ? '6px 8px 5px' : '7px 10px 6px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
              <span style={{ color: '#67d9ff', fontSize: 12 }}>{currentGroup.icon}</span>
              <span style={{
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: '#f2f5f7', fontSize: narrowHud ? 10 : 12, fontWeight: 900, letterSpacing: 1.2,
              }}>{cur?.label || 'NO UNIT'}</span>
              <span style={{ marginLeft: 'auto', flexShrink: 0, color: '#8da0aa', fontSize: 9, letterSpacing: 1 }}>
                {groupIndex + 1}/{Math.max(1, currentGroupUnits.length)}
              </span>
            </div>
            <div style={{ height: 3, marginTop: 5, background: 'rgba(255,255,255,0.12)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ width: `${hpFrac * 100}%`, height: '100%', background: hpColor }} />
            </div>
            <div data-testid="command-order" style={{
              marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: cur?.order === 'attack' ? '#ff9f73' : '#f0c777',
              fontSize: narrowHud ? 8 : 9, fontWeight: 800, letterSpacing: narrowHud ? 0.8 : 1.25,
            }}>{cur?.orderLabel || 'AWAITING ORDERS'}</div>
          </div>
          {arrowBtn('›', 1)}
        </div>
      </div>
      <button
        data-testid="crisis-jump"
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onJumpCrisis(); }}
        title="Jump to crisis (Y / F)"
        style={{
          width: narrowHud ? 56 : 62, flexShrink: 0, borderRadius: 5,
          background: alert
            ? (alert.severity === 'high' ? 'linear-gradient(180deg, #9f3a28, #5a1d16)' : 'linear-gradient(180deg, #9c6724, #563411)')
            : 'linear-gradient(180deg, rgba(75,55,28,0.94), rgba(26,19,12,0.96))',
          border: alert ? '1px solid #ffd08a' : '1px solid rgba(207,160,82,0.68)',
          color: alert ? '#fff4d6' : '#d8b36e', font: '900 9px/1.05 inherit', letterSpacing: 1,
          cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
          boxShadow: alert ? 'inset 0 0 12px rgba(255,150,55,0.22)' : 'none',
        }}>
        <span style={{ fontSize: 16 }}>⚠</span>
        <span>JUMP</span>
      </button>
    </div>
  );
}

function ControlHint() {
  const hint = useStore((s) => s.controlHint);
  const at = useStore((s) => s.controlHintAt);
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    const id = setInterval(() => force(), 100);
    return () => clearInterval(id);
  }, []);
  if (!hint) return null;
  const elapsed = performance.now() / 1000 - at;
  if (elapsed > 3.2) return null;
  const opacity = elapsed < 0.15 ? elapsed / 0.15 : elapsed > 2.6 ? Math.max(0, 1 - (elapsed - 2.6) / 0.6) : 1;
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, top: '63%',
      textAlign: 'center', pointerEvents: 'none', opacity,
    }}>
      <span style={{
        display: 'inline-block',
        background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(180,210,255,0.5)',
        borderRadius: 6, padding: '6px 14px',
        font: '700 13px/1 inherit', letterSpacing: 2, color: '#cfe8ff',
        textShadow: '0 1px 2px #000',
      }}>{hint}</span>
    </div>
  );
}

function MapButton({ onToggleMap }: { onToggleMap: () => void }) {
  return (
    <button
      data-testid="map-button"
      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onToggleMap(); }}
      title="Tactical map (X / □)"
      style={{
        position: 'absolute', right: 10, top: 52,
        padding: '6px 10px', background: 'rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.35)', borderRadius: 4,
        color: '#fff', font: '700 12px/1 inherit', letterSpacing: 1,
        cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
      }}>🗺 MAP</button>
  );
}

// Map glyph language: SHAPE = role (so you don't depend on colour), COLOUR = side.
//   plane = triangle · tank = square · AA gun = ringed dot · zeppelin = capsule · village = house.
function MapGlyph({ kind, x, y, col, s = 1 }: { kind: FriendlyKind | 'zeppelin'; x: number; y: number; col: string; s?: number }) {
  if (kind === 'tank') return <rect x={x - 8 * s} y={y - 6 * s} width={16 * s} height={12 * s} rx={1.5} fill={col} stroke="#0b0f08" strokeWidth={1.5} />;
  if (kind === 'gun') return <g><circle cx={x} cy={y} r={7 * s} fill="none" stroke={col} strokeWidth={2.4} /><circle cx={x} cy={y} r={2.4 * s} fill={col} /></g>;
  if (kind === 'zeppelin') return <rect x={x - 12 * s} y={y - 5 * s} width={24 * s} height={10 * s} rx={5 * s} fill={col} stroke="#0b0f08" strokeWidth={1.2} />;
  // plane — triangle pointing up
  return <polygon points={`${x},${y - 10 * s} ${x + 8 * s},${y + 7 * s} ${x - 8 * s},${y + 7 * s}`} fill={col} stroke="#0b0f08" strokeWidth={1.5} />;
}

function TacticalMap({ onSelectUnit, onToggleMap, onIssueOrder }: {
  onSelectUnit: (id: number) => void;
  onToggleMap: () => void;
  onIssueOrder: (unitId: number, order: UnitOrder) => void;
}) {
  const P = usePal();
  const open = useStore((s) => s.mapOpen);
  const data = useStore((s) => s.mapData);
  // Selection + cursor live in the store so pointer and gamepad drive the same state.
  const selected = useStore((s) => s.mapSelectedId);
  const cursor = useStore((s) => s.mapCursor);
  const setSelected = useStore((s) => s.setMapSelected);
  const [, force] = React.useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(() => force(), 140); // live refresh + pulse
    return () => clearInterval(id);
  }, [open]);
  if (!open || !data) return null;

  const V = 1000, C = V / 2, span = 460, R = data.worldRadius;
  const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 1000 * 5);
  const sx = (x: number) => C + (x / R) * span;
  const sy = (z: number) => C + (z / R) * span;

  const sel = selected != null ? data.friendlies.find((f) => f.id === selected) : undefined;
  const canOrder = !!sel && sel.kind !== 'gun'; // fixed AA guns can't be ordered to move

  const order = (o: UnitOrder) => { if (sel) { onIssueOrder(sel.id, o); setSelected(null); } };
  const pilot = (id: number) => { onSelectUnit(id); onToggleMap(); };

  return (
    <div
      data-testid="tactical-map"
      onPointerDown={(e) => { e.stopPropagation(); setSelected(null); }}
      style={{
        position: 'absolute', inset: 0, zIndex: 20,
        background: 'rgba(6,10,8,0.92)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        pointerEvents: 'auto',
      }}>
      <div style={{
        marginTop: 12, font: '800 16px/1 inherit', letterSpacing: 3, color: '#cfe8ff', textShadow: '0 1px 2px #000',
      }}>TACTICAL MAP <span style={{ opacity: 0.6, fontSize: 12, letterSpacing: 1 }}>— LIVE</span></div>
      <div style={{ fontSize: 11, opacity: 0.75, letterSpacing: 1, color: '#bcd', marginTop: 3, textAlign: 'center' }}>
        {canOrder
          ? 'tap a VILLAGE or UNIT to defend · an ENEMY to attack · or a button below'
          : 'tap one of your units to command it'}
        <div style={{ opacity: 0.7, marginTop: 2 }}>
          {COARSE_POINTER
            ? '🎮 stick: cursor · Ⓐ select/order · Ⓨ pilot · Ⓑ back · Ⓧ close · LB/RB: pick unit'
            : '⌨ WASD: cursor · ENTER select/order · F pilot · ESC back · M close · Q/E pick unit'}
        </div>
      </div>

      <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
        <svg viewBox={`0 0 ${V} ${V}`} style={{ width: 'min(92vw, 72vh)', height: 'min(92vw, 72vh)' }}>
          <defs>
            <radialGradient id="mapBg" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#13241a" />
              <stop offset="100%" stopColor="#0a140d" />
            </radialGradient>
          </defs>
          <circle cx={C} cy={C} r={span * (data.ringRadius / R)} fill="url(#mapBg)" stroke="#6b7a52" strokeWidth={2} />
          <circle cx={C} cy={C} r={span * (data.ringRadius / R) * 0.66} fill="none" stroke="rgba(120,200,140,0.18)" strokeWidth={1} />
          <circle cx={C} cy={C} r={span * (data.ringRadius / R) * 0.33} fill="none" stroke="rgba(120,200,140,0.18)" strokeWidth={1} />
          <text x={C} y={C - span - 6} fill="#9bbf8f" fontSize={22} fontWeight={800} textAnchor="middle">N</text>

          {/* "Currently targeting" lines — the enemy each unit is pursuing/firing on right
              now (pale cyan), distinct from the bold green/orange standing-order lines. */}
          {data.friendlies.map((f) => {
            if (!f.engage) return null;
            return <line key={`g${f.id}`} x1={sx(f.x)} y1={sy(f.z)} x2={sx(f.engage.x)} y2={sy(f.engage.z)}
              stroke="#9fe0ff" strokeWidth={1.6} strokeDasharray="4 4" opacity={0.6} />;
          })}

          {/* Order lines — the standing plan: green = defend/escort, orange = attack. */}
          {data.friendlies.map((f) => {
            if (f.order.tx == null || f.order.tz == null) return null;
            const col = f.order.type === 'attack' ? '#ff7a5a' : P.friendly;
            return <line key={`o${f.id}`} x1={sx(f.x)} y1={sy(f.z)} x2={sx(f.order.tx)} y2={sy(f.order.tz)}
              stroke={col} strokeWidth={1.8} strokeDasharray="6 5" opacity={0.7} />;
          })}

          {/* Villages — house glyph, colour by damage; pulse if under attack; tap = defend. */}
          {data.villages.map((v) => {
            const x = sx(v.x), y = sy(v.z);
            const base = v.destroyed ? '#4a4a45'
              : v.damageFrac > 0.5 ? '#ff5a3a'
              : v.damageFrac > 0.2 ? '#ffae3a' : '#9bd49b';
            // Under attack: flash the icon between its damage colour and bright red so it's
            // impossible to miss, plus an expanding alarm ring.
            const fill = v.threatened ? (pulse > 0.5 ? '#ff2a2a' : base) : base;
            const sc = v.threatened ? 1 + pulse * 0.35 : 1;
            return (
              <g key={`v${v.index}`} data-testid={`map-village-${v.index}`}
                 data-threatened={v.threatened ? 'true' : 'false'}
                 style={{ cursor: canOrder ? 'crosshair' : 'default' }}
                 onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); if (canOrder) order({ type: 'defend', villageIndex: v.index }); }}>
                {canOrder && <circle cx={x} cy={y} r={22} fill="transparent" />}
                {v.threatened && <circle cx={x} cy={y} r={14 + pulse * 12} fill="none" stroke="#ff2a2a" strokeWidth={2.5} opacity={0.3 + pulse * 0.5} />}
                {/* house: square body + roof peak, scaled up when alarming */}
                <polygon points={`${x - 7 * sc},${y + 7 * sc} ${x - 7 * sc},${y - 2 * sc} ${x},${y - 9 * sc} ${x + 7 * sc},${y - 2 * sc} ${x + 7 * sc},${y + 7 * sc}`}
                  fill={fill} stroke={v.threatened ? '#ffd0d0' : '#11160d'} strokeWidth={1.4} opacity={v.destroyed ? 0.5 : 1} />
              </g>
            );
          })}

          {/* Enemies — red; tap = order selected unit to attack. */}
          {data.enemies.map((e) => {
            const x = sx(e.x), y = sy(e.z);
            const col = e.dying ? '#888' : P.enemy;
            const glyphKind = e.kind === 'zeppelin' ? 'zeppelin' : e.kind === 'tank' ? 'tank' : 'plane';
            return (
              <g key={`e${e.id}`} style={{ cursor: canOrder && !e.dying ? 'crosshair' : 'default' }}
                 onPointerDown={(ev) => { ev.stopPropagation(); ev.preventDefault(); if (canOrder && !e.dying) order({ type: 'attack', targetId: e.id }); }}>
                {canOrder && !e.dying && <circle cx={x} cy={y} r={20} fill="transparent" />}
                <g opacity={e.dying ? 0.5 : 0.97}><MapGlyph kind={glyphKind} x={x} y={y} col={col} s={0.85} /></g>
              </g>
            );
          })}

          {/* Friendlies — green; gun blue; you/selected ringed; threatened pulses red; tap = select. */}
          {data.friendlies.map((f) => {
            const x = sx(f.x), y = sy(f.z);
            const col = f.controlled ? '#ffe14a' : f.kind === 'gun' ? P.gun : P.friendly;
            const isSel = f.id === selected;
            return (
              <g key={`f${f.id}`} data-testid={`map-friendly-${f.kind}-${f.id}`}
                 data-controlled={f.controlled ? 'true' : 'false'} style={{ cursor: 'pointer' }}
                 onPointerDown={(e) => {
                   e.stopPropagation(); e.preventDefault();
                   // Verb-object: with another unit selected, tapping a friendly orders it
                   // to escort this one; otherwise select (tap self to deselect).
                   if (selected != null && selected !== f.id && canOrder) order({ type: 'escort', unitId: f.id });
                   else setSelected(isSel ? null : f.id);
                 }}>
                <circle cx={x} cy={y} r={24} fill="transparent" />
                {f.threatened && <circle cx={x} cy={y} r={15 + pulse * 6} fill="none" stroke="#ff5a3a" strokeWidth={2} opacity={0.5 + pulse * 0.5} />}
                {isSel && <circle cx={x} cy={y} r={16} fill="none" stroke="#fff" strokeWidth={2.5} />}
                {f.controlled && <circle cx={x} cy={y} r={13} fill="none" stroke="#ffe14a" strokeWidth={2.2} />}
                <MapGlyph kind={f.kind} x={x} y={y} col={col} />
                <rect x={x - 10} y={y + 11} width={20} height={3} fill="rgba(0,0,0,0.5)" />
                <rect x={x - 10} y={y + 11} width={20 * f.hpFrac} height={3}
                  fill={f.hpFrac <= 0.3 ? '#e74c3c' : f.hpFrac <= 0.6 ? '#f39c12' : '#2ecc71'} />
              </g>
            );
          })}

          {/* Gamepad command cursor — a reticle you steer with the left stick. */}
          {cursor && (() => {
            const x = sx(cursor.x), y = sy(cursor.z);
            return (
              <g opacity={0.9}>
                <circle cx={x} cy={y} r={13} fill="none" stroke="#ffffff" strokeWidth={1.6} />
                <line x1={x - 18} y1={y} x2={x - 7} y2={y} stroke="#ffffff" strokeWidth={1.6} />
                <line x1={x + 7} y1={y} x2={x + 18} y2={y} stroke="#ffffff" strokeWidth={1.6} />
                <line x1={x} y1={y - 18} x2={x} y2={y - 7} stroke="#ffffff" strokeWidth={1.6} />
                <line x1={x} y1={y + 7} x2={x} y2={y + 18} stroke="#ffffff" strokeWidth={1.6} />
              </g>
            );
          })()}
        </svg>
      </div>

      {/* Command bar — when a unit is selected, act on it; otherwise show the legend. */}
      {sel ? (
        <div onPointerDown={(e) => e.stopPropagation()}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'center', padding: '4px 12px' }}>
          <span style={{ font: '800 13px/1 inherit', letterSpacing: 1, color: '#ffe14a' }}>{sel.label}</span>
          <span style={{ font: '600 11px/1 inherit', letterSpacing: 1, color: '#9fb6c9' }}>
            {sel.order.type === 'defend' ? 'defending' : sel.order.type === 'escort' ? 'escorting' : sel.order.type === 'attack' ? 'attacking' : 'on patrol'}
          </span>
          <CmdBtn label="✈ PILOT" tone="#2e6da4" onTap={() => pilot(sel.id)} />
          {canOrder && <CmdBtn label="PATROL" tone="#3a4a2a" onTap={() => order({ type: 'patrol' })} />}
          {!canOrder && <span style={{ font: '600 11px/1 inherit', color: '#9fb6c9' }}>(fixed emplacement — defends its town)</span>}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', padding: '0 12px', font: '600 11px/1.4 inherit', color: '#bcd', letterSpacing: 1 }}>
          <span style={{ color: P.friendly }}>▲ fighter</span>
          <span style={{ color: P.gun }}>⊙ AA gun</span>
          <span style={{ color: P.friendly }}>■ tank</span>
          <span style={{ color: '#9bd49b' }}>⌂ village</span>
          <span style={{ color: P.enemy }}>▲ enemy</span>
          <span style={{ color: '#ff5a3a' }}>◌ under attack</span>
          <span style={{ color: '#9fe0ff' }}>┄ targeting</span>
          <span style={{ color: P.friendly }}>┄ defend</span>
          <span style={{ color: '#ff7a5a' }}>┄ attack</span>
        </div>
      )}
      <button
        data-testid="map-close"
        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onToggleMap(); }}
        style={{
          margin: '8px 0 16px', padding: '9px 26px',
          background: 'rgba(46,109,164,0.9)', color: '#fff',
          border: '1px solid rgba(160,200,255,0.6)', borderRadius: 6,
          font: '800 14px/1 inherit', letterSpacing: 2,
          cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
        }}>CLOSE ✕</button>
    </div>
  );
}

function CmdBtn({ label, tone, onTap }: { label: string; tone: string; onTap: () => void }) {
  return (
    <button
      onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); onTap(); }}
      style={{
        padding: '7px 14px', background: tone, color: '#fff',
        border: '1px solid rgba(255,255,255,0.35)', borderRadius: 5,
        font: '800 12px/1 inherit', letterSpacing: 1,
        cursor: 'pointer', pointerEvents: 'auto', touchAction: 'none',
      }}>{label}</button>
  );
}

// Small-radar glyph: same shape language as the map (shape = role, colour = side).
function RadarGlyph({ kind, cx, cy, col }: { kind: string; cx: number; cy: number; col: string }) {
  const stroke = '#0a0a0a';
  if (kind === 'tank') return <rect x={cx - 3.4} y={cy - 2.6} width={6.8} height={5.2} rx={0.6} fill={col} stroke={stroke} strokeWidth={0.6} />;
  if (kind === 'gun') return <g><circle cx={cx} cy={cy} r={3} fill="none" stroke={col} strokeWidth={1.3} /><circle cx={cx} cy={cy} r={1.1} fill={col} /></g>;
  if (kind === 'zeppelin') return <rect x={cx - 5} y={cy - 2.2} width={10} height={4.4} rx={2.2} fill={col} stroke={stroke} strokeWidth={0.5} />;
  return <polygon points={`${cx},${cy - 3.6} ${cx + 3},${cy + 2.8} ${cx - 3},${cy + 2.8}`} fill={col} stroke={stroke} strokeWidth={0.6} />; // plane / enemy
}

function Radar() {
  const P = usePal();
  // Subscribe to tick so we re-render at ~12 Hz; read blips directly (they're an in-place array).
  useStore((s) => s.radarTick);
  const st = useStore.getState();
  const blips = st.radarBlips;
  const friendlies = st.radarFriendlies;
  const villages = st.radarVillages;

  // Radar covers WORLD_RADIUS in every direction from the player. Bumped up a touch
  // since it now shows the whole force + every village — the player needs to read it.
  const WORLD_RADIUS = 4500;
  const size = 152;
  const half = size / 2;
  const maxR = half - 4;
  const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 1000 * 5);

  /** Map (rx, rz) world units to radar pixel coords. Clamps to the rim with a flag. */
  const project = (rx: number, rz: number) => {
    let bx = (rx / WORLD_RADIUS) * maxR;
    let by = -(rz / WORLD_RADIUS) * maxR;
    const mag = Math.hypot(bx, by);
    const clamped = mag > maxR;
    if (clamped) { const k = maxR / mag; bx *= k; by *= k; }
    return { cx: half + bx, cy: half + by, clamped };
  };

  return (
    <div style={{
      position: 'absolute',
      top: 42, left: 10,
      width: size, height: size,
      pointerEvents: 'none',
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }}>
        {/* Background disc */}
        <defs>
          <radialGradient id="radarBg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(30,50,30,0.82)" />
            <stop offset="100%" stopColor="rgba(10,25,15,0.92)" />
          </radialGradient>
        </defs>
        <circle cx={half} cy={half} r={half - 1} fill="url(#radarBg)" stroke="rgba(120,200,140,0.75)" strokeWidth="1.5" />
        {/* Range rings */}
        <circle cx={half} cy={half} r={(half - 1) * 0.66} fill="none" stroke="rgba(120,200,140,0.25)" strokeWidth="1" />
        <circle cx={half} cy={half} r={(half - 1) * 0.33} fill="none" stroke="rgba(120,200,140,0.25)" strokeWidth="1" />
        {/* Crosshairs */}
        <line x1={half} y1="4" x2={half} y2={size - 4} stroke="rgba(120,200,140,0.18)" strokeWidth="1" />
        <line x1="4" y1={half} x2={size - 4} y2={half} stroke="rgba(120,200,140,0.18)" strokeWidth="1" />
        {/* Villages — render UNDER everything else so units overlay them. Colour shifts
            from green → yellow → red as buildings fall; grey when fully destroyed. */}
        {villages.map((v, i) => {
          const { cx, cy, clamped } = project(v.rx, v.rz);
          if (clamped) return null; // villages off-radar aren't actionable
          const live = !v.destroyed;
          const fill = v.destroyed
            ? '#5a5a55'
            : v.damageFrac > 0.5 ? '#ff5a3a'
            : v.damageFrac > 0.2 ? '#ffae3a'
            : '#a3e6a3';
          const ring = v.threatened && live ? (
            <circle cx={cx} cy={cy} r={6 + pulse * 4}
              fill="none" stroke="#ff5a3a" strokeWidth={1.2} opacity={0.45 + pulse * 0.4} />
          ) : null;
          return (
            <g key={`v${i}`} opacity={live ? 1 : 0.55}>
              {ring}
              <rect x={cx - 3} y={cy - 3} width={6} height={6}
                fill={fill} stroke="#1a1a14" strokeWidth={0.6} />
            </g>
          );
        })}
        {/* Enemies — red, SHAPE = role: plane triangle · tank square · zeppelin capsule. */}
        {blips.map((b, i) => {
          const { cx, cy, clamped } = project(b.rx, b.rz);
          const live = !b.dying;
          const col = !live ? '#888' : clamped ? '#ff6b00' : P.enemy;
          return <g key={`e${i}`} opacity={live ? 1 : 0.5}><RadarGlyph kind={b.kind} cx={cx} cy={cy} col={col} /></g>;
        })}
        {/* Friendlies — green (you = yellow); same role-shapes; threatened pulses red. */}
        {friendlies.map((f, i) => {
          const { cx, cy } = project(f.rx, f.rz);
          const col = f.controlled ? '#ffe14a' : f.kind === 'gun' ? P.gun : P.friendly;
          return (
            <g key={`f${i}`}>
              {f.threatened && <circle cx={cx} cy={cy} r={6 + pulse * 3} fill="none" stroke="#ff5a3a" strokeWidth={1.2} opacity={0.5 + pulse * 0.5} />}
              {f.controlled && <circle cx={cx} cy={cy} r={5.5} fill="none" stroke="#ffe14a" strokeWidth={1.3} />}
              <RadarGlyph kind={f.kind} cx={cx} cy={cy} col={col} />
            </g>
          );
        })}
        {/* Player (centered, always pointing up) */}
        <polygon points={`${half},${half - 6} ${half - 4},${half + 4} ${half + 4},${half + 4}`}
          fill="#e0e0e0" stroke="#111" strokeWidth="0.6" />
      </svg>
    </div>
  );
}

function EnemyArrow({ angle }: { angle: number }) {
  const P = usePal();
  // Place the arrow on an ellipse near the screen edge, at the given angle.
  // angle: 0 = right, π/2 = up, etc.
  const r = 38; // percent of min(vw,vh) / 2
  const cx = 50 + Math.cos(angle) * r;
  const cy = 50 - Math.sin(angle) * r;
  // Rotate the triangle to point outward.
  const rotDeg = (-angle * 180) / Math.PI + 90;
  return (
    <div style={{
      position: 'absolute',
      left: `${cx}%`,
      top: `${cy}%`,
      transform: `translate(-50%, -50%) rotate(${rotDeg}deg)`,
      pointerEvents: 'none',
      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
    }}>
      <svg width="44" height="44" viewBox="0 0 44 44">
        <polygon points="22,4 36,32 22,26 8,32" fill={P.enemy} stroke="#fff" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function Splash({ onTutorial }: { onTutorial: () => void }) {
  const [pulse, setPulse] = React.useState(0);
  const [showHow, setShowHow] = React.useState(false);
  const bestWave = useStore((s) => s.prog.bestWave);
  const hiScore = useStore((s) => s.hiScore);
  React.useEffect(() => {
    const id = setInterval(() => setPulse((p) => p + 1), 650);
    return () => clearInterval(id);
  }, []);
  const bright = pulse % 2 === 0;
  if (showHow) return <HowToPlay onBack={() => setShowHow(false)} />;
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: '#fff',
      textAlign: 'center',
      gap: 14,
      padding: '24px 16px',
      pointerEvents: 'none',
      textShadow: '0 2px 8px rgba(0,0,0,0.9)',
    }}>
      <div style={{ fontSize: 34, letterSpacing: 6, fontWeight: 700 }}>STEEL TEMPEST</div>
      <div style={{ fontSize: 14, letterSpacing: 2, opacity: 0.85, marginTop: -8 }}>STEAMPUNK SKIES — AERIAL COMMANDER</div>

      {/* The objective in two short lines — every other detail lives in HOW TO PLAY. */}
      <div style={{
        maxWidth: 320, fontSize: 13, lineHeight: 1.5, letterSpacing: 0.5,
        color: '#ffe6b0',
        background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,210,140,0.35)',
        borderRadius: 8, padding: '8px 14px',
      }}>
        <b>DEFEND YOUR VILLAGES.</b> Survive all <b>24 waves</b> to win the war.
      </div>

      {(bestWave > 0 || hiScore > 0) && (
        <div style={{ fontSize: 12, letterSpacing: 2, opacity: 0.8, marginTop: -4 }}>
          BEST&nbsp;WAVE <b>{bestWave}</b>&nbsp;&nbsp;·&nbsp;&nbsp;HIGH&nbsp;SCORE <b>{hiScore.toLocaleString()}</b>
        </div>
      )}

      <div style={{ marginTop: 8, position: 'relative', width: 96, height: 96 }}>
        <div style={{
          position: 'absolute', inset: 0,
          border: '3px solid rgba(255,255,255,0.7)',
          borderRadius: '50%',
          opacity: bright ? 1 : 0.5,
          transition: 'opacity 0.3s',
        }} />
        <div style={{
          position: 'absolute',
          width: 16, height: 16,
          top: bright ? 14 : 56,
          left: bright ? 40 : 68,
          background: '#fff', borderRadius: '50%',
          transition: 'top 0.5s ease-in-out, left 0.5s ease-in-out',
        }} />
      </div>

      <div style={{ fontSize: 17, letterSpacing: 3, fontWeight: 700 }}>
        {COARSE_POINTER ? <>TAP ANYWHERE &amp; DRAG TO FLY</> : <>DRAG TO FLY · WASD + SPACE</>}
      </div>
      <div style={{ fontSize: 13, letterSpacing: 4, opacity: bright ? 0.9 : 0.4, fontWeight: 700 }}>
        {COARSE_POINTER ? 'TAP OR PRESS ✕ / A' : 'PRESS ENTER'}
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button
          data-testid="start-tutorial"
          onClick={(e) => { e.stopPropagation(); onTutorial(); }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(40,140,60,0.9)', color: '#fff',
            border: '1px solid rgba(160,255,180,0.7)', borderRadius: 6,
            padding: '10px 20px', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
            letterSpacing: 2, cursor: 'pointer',
          }}>
          ▶ TUTORIAL
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setShowHow(true); }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(0,0,0,0.5)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.45)', borderRadius: 6,
            padding: '10px 20px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            letterSpacing: 2, cursor: 'pointer',
          }}>
          ❓ HOW TO PLAY
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); useStore.getState().setPhase('hangar'); }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(46,109,164,0.85)', color: '#fff',
            border: '1px solid rgba(160,200,255,0.6)', borderRadius: 6,
            padding: '10px 20px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
            letterSpacing: 2, cursor: 'pointer',
          }}>
          ⚙ HANGAR
        </button>
      </div>
    </div>
  );
}

/** Static illustrated "How to Play" reference — a scrollable panel of control + commander +
 *  objective sections. Reached from the splash; BACK returns to it. */
function HowToPlay({ onBack }: { onBack: () => void }) {
  const Section = ({ title, lines }: { title: string; lines: string[] }) => (
    <div style={{ marginBottom: 14, textAlign: 'left', maxWidth: 460 }}>
      <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 2, color: '#ffe6b0', marginBottom: 4 }}>{title}</div>
      {lines.map((l, i) => (
        <div key={i} style={{ fontSize: 12.5, lineHeight: 1.55, color: '#dfe7ee', opacity: 0.92 }}
          dangerouslySetInnerHTML={{ __html: l }} />
      ))}
    </div>
  );
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'auto',
      background: 'rgba(6,10,16,0.92)', color: '#fff',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      overflowY: 'auto', padding: '28px 18px',
      textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    }}>
      <div style={{ fontSize: 26, letterSpacing: 4, fontWeight: 800, marginBottom: 4 }}>HOW TO PLAY</div>
      <div style={{ fontSize: 12, letterSpacing: 1, opacity: 0.7, marginBottom: 20 }}>STEEL TEMPEST — quick reference</div>
      <Section title="✈ FLY" lines={[
        '<b>Touch:</b> drag anywhere — the plane follows the drag. <b>Gamepad:</b> left stick. <b>Keyboard:</b> WASD / arrows (or mouse-drag).',
        '<b>Pull back</b> to climb, <b>push forward</b> to dive. Bank to turn.',
        '<b>Hold FIRE</b> (RT / SPACE) to shoot — twin guns, leads the target. Your fire can hit friendlies + villages, so aim.',
        '<b>BOOST</b> button (LT — analog · SHIFT) for a speed burst across the map.',
      ]} />
      <Section title="🎖 COMMAND THE FORCE" lines={[
        'You command a whole squadron — fighters, AA guns and tanks.',
        '<b>◀ ▶</b> (LB/RB · Q/E) cycle which unit you crew. The <b>⚠</b> button (Y · F) jumps to the hottest fight.',
        'Open the <b>🗺 MAP</b> (M) to see the whole front; tap any unit to take command, or drag from a unit to a target to order it.',
        'Units you’re not crewing fight on their own. <b>AUTOPILOT</b> flies only the unit you currently crew.',
      ]} />
      <Section title="🔫 GROUND UNITS" lines={[
        '<b>AA gun:</b> stick aims, <b>FIRE</b>/RT shoots (it only auto-fires when left to its AI crew).',
        '<b>Tank:</b> left stick drives, right stick traverses the turret; the targeting computer marks the lead — line the crosshair on it and <b>FIRE</b>.',
      ]} />
      <Section title="🏰 OBJECTIVE" lines={[
        '<b>Defend your villages.</b> Enemy bombers and tanks try to level them.',
        'Score milestones earn <b>reinforcement</b> planes (they arrive on a cooldown).',
        'You lose if <b>every village</b> falls — or your <b>whole squadron</b> is wiped out.',
      ]} />
      <button
        onClick={onBack}
        style={{
          marginTop: 8, marginBottom: 24, pointerEvents: 'auto',
          background: 'rgba(46,109,164,0.9)', color: '#fff',
          border: '1px solid rgba(160,200,255,0.6)', borderRadius: 6,
          padding: '10px 26px', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
          letterSpacing: 2, cursor: 'pointer',
        }}>
        ◀ BACK
      </button>
    </div>
  );
}
